import React from 'react';
import {Anchor, Icon, Menu, Text} from '@albert-io/atomic';

import {restrictionRemovalMessage} from './manageRestrictionsModal.constants';
import {formatRemovalDate, getRestrictionRemovalDate} from './proExpiry';
import type {AdminSchool} from './useAdminSchools';

const SchoolGroup = ({
  heading,
  schools,
  showRemovalWarnings = false
}: {
  heading: string;
  schools: AdminSchool[];
  /** Grace-window removal warnings only make sense under schools that restrict this content. */
  showRemovalWarnings?: boolean;
}) => (
  <div>
    <Text bold size='m'>
      {heading}
    </Text>
    <div className='mt-x1 gap-y-x1 flex flex-col font-normal'>
      {schools.map((school) => {
        const removalDate = showRemovalWarnings ? getRestrictionRemovalDate(school) : null;
        return (
          <div key={school.id}>
            <Text as='div' size='m'>
              {school.name}
            </Text>
            {removalDate && (
              <Text
                as='div'
                size='xs'
                color='warning'
                data-testid={`restriction-menu-${school.id}__removal-warning`}
              >
                <Icon
                  icon='exclamation-triangle'
                  iconStyle='regular'
                  color='warning'
                  aria-hidden
                  className='mr-x0-half'
                />
                {restrictionRemovalMessage(formatRemovalDate(removalDate))}
              </Text>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

interface RestrictionMenuProps {
  restricted: AdminSchool[];
  unrestricted: AdminSchool[];
  /** Guide-surface CTA: opens the manage-restrictions modal inline ("Edit Settings"). */
  onEdit?: () => void;
  /**
   * Question-surface CTA: links out to the assessment guide (restrictions are managed there rather
   * than inline) — signalled with the external-link icon. Ignored when `onEdit` is provided.
   */
  assessmentGuideHref?: string;
  /** Invoked after the CTA is actioned so the surrounding popover can close itself. */
  onClose?: () => void;
}

/**
 * Per-school restricted/unrestricted breakdown shown when an admin opens a restriction indicator
 * (DQA #17841). One shared design for both surfaces; only the CTA differs — guide surfaces open the
 * manage-restrictions modal inline (`onEdit`), question surfaces link out to the assessment guide
 * (`assessmentGuideHref`).
 */
export const RestrictionMenu = ({
  restricted,
  unrestricted,
  onEdit,
  assessmentGuideHref,
  onClose
}: RestrictionMenuProps) => (
  <div role='menu' aria-label='Restriction details' className='flex flex-col text-left'>
    {/* The school lists scroll past 420px; the Edit Settings row stays pinned below (Figma
        3414-66434). */}
    <div className='max-h-[420px] overflow-y-auto'>
      {restricted.length > 0 && (
        <Menu.Item readonly>
          <SchoolGroup heading='Restricted at' schools={restricted} showRemovalWarnings />
        </Menu.Item>
      )}
      {unrestricted.length > 0 && (
        <Menu.Item readonly>
          <SchoolGroup heading='Unrestricted at' schools={unrestricted} />
        </Menu.Item>
      )}
    </div>
    {onEdit ? (
      <>
        <Menu.Divider />
        <Menu.Item
          onClick={() => {
            onEdit();
            onClose?.();
          }}
          leftIconProps={{icon: 'cog', iconStyle: 'regular'}}
          data-testid='restriction-menu__edit-settings'
        >
          Edit Settings
        </Menu.Item>
      </>
    ) : (
      assessmentGuideHref && (
        <>
          <Menu.Divider />
          <div role='none' className='px-x1-half py-x1 font-bold'>
            <Anchor
              role='menuitem'
              to={assessmentGuideHref}
              noUnderline
              icon='arrow-up-right-from-square'
              iconPosition='right'
              onClick={() => onClose?.()}
            >
              Edit restriction settings in the assessment guide
            </Anchor>
          </div>
        </>
      )
    )}
  </div>
);
